import { useState, useRef, useEffect, useMemo } from "react";
import { Lock, X, Sparkles, Replace, Power, Trash2, Check } from "lucide-react";

/**
 * SubjectLockPill — surfaces the session's locked subject(s) so users always
 * know which identity (or identities) are being carried across generations.
 *
 * Multi-subject (Feb 2026):
 *   · The swap picker supports multi-select — tap feed images to toggle
 *     them into the lock. Selected tiles get a cyan ring + check badge.
 *   · Caps at MAX_SUBJECTS (5) to match the Omni reference limit.
 *   · APPLY (N) commits the picks. APPLY with 0 picks → clears the lock.
 *   · The pill thumb shows a small "+N" badge when extra subjects are active.
 *   · Header reads "SUBJECT LOCKED · N SELECTED" when N > 1.
 */
const MAX_SUBJECTS = 5;

export function SubjectLockPill({
  lock,                 // { url, uploaded_url, uploaded_urls?: string[], source, prompt, enabled? } | null
  onClear,
  onReplace,            // (item) => void — single-pick (back-compat; still used for 1-tap)
  onApplyMulti,         // (items[]) => void — multi-pick commit
  onRemoveLockedUrl,    // (url: string) => void — detach a single URL from the active lock
  onToggle,             // (nextEnabled: boolean) => void
  pastItems = [],
  className = "",
  testId = "subject-lock-pill",
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]); // feed item ids staged for APPLY
  const rootRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Reset staged picks every time the picker opens so prior state doesn't leak
  useEffect(() => {
    if (pickerOpen) setSelectedIds([]);
  }, [pickerOpen]);

  const enabled = lock?.enabled !== false; // undefined → treat as ON (back-compat)

  // Primary + extras. `uploaded_urls` is the canonical multi-source; falls
  // back to the legacy single `uploaded_url` so older lock objects still work.
  const lockedUrls = useMemo(() => {
    if (!lock) return [];
    if (Array.isArray(lock.uploaded_urls) && lock.uploaded_urls.length) return lock.uploaded_urls;
    return lock.uploaded_url ? [lock.uploaded_url] : [];
  }, [lock]);
  const extraCount = Math.max(0, lockedUrls.length - 1);

  // Eligible swap targets: done image items whose media_url isn't already locked
  const targets = useMemo(() => (
    pastItems.filter(
      (it) =>
        it.status === "done" &&
        it.kind === "image" &&
        it.media_url &&
        !lockedUrls.includes(it.media_url)
    )
  ), [pastItems, lockedUrls]);

  if (!lock) return null;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_SUBJECTS) return prev; // soft cap — ignore extra taps
      return [...prev, id];
    });
  };

  const commitApply = () => {
    const picked = selectedIds
      .map((id) => targets.find((t) => t.id === id))
      .filter(Boolean);
    if (picked.length === 0) {
      // APPLY with 0 picks clears the lock entirely (per product decision)
      setPickerOpen(false);
      onClear?.();
      return;
    }
    onApplyMulti?.(picked);
    setPickerOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={`fixed right-4 sm:right-6 bottom-[280px] z-50 pointer-events-none ${className}`}
      data-testid={testId}
    >
      {/* Swap-subject picker rises above the pill. Right-anchored so it
          stays inside the viewport when the pill sits on the right edge. */}
      {pickerOpen && (
        <div
          className="pointer-events-auto mb-2 ml-auto rounded-2xl border border-white/[0.08] bg-[#0a0812]/95 backdrop-blur-2xl shadow-[0_0_44px_-12px_rgba(0,229,255,0.55)] overflow-hidden max-w-[560px]"
          data-testid="subject-lock-picker"
        >
          <div className="px-3.5 py-2 flex items-center justify-between gap-3 border-b border-white/[0.06]">
            <div className="font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.22em] flex items-center gap-1.5">
              <Replace className="w-3 h-3" /> SWAP SUBJECT · PICK FROM YOUR FEED
            </div>
            <div className="flex items-center gap-1.5">
              {/* APPLY pill — commits the staged picks (or clears if 0) */}
              <button
                onClick={commitApply}
                data-testid="subject-lock-picker-apply"
                title={selectedIds.length ? "Apply selected subjects" : "Clear the current subject lock"}
                className={`inline-flex items-center gap-1 h-6 px-2.5 rounded-full border font-mono-hy text-[9px] tracking-[0.18em] transition-all ${
                  selectedIds.length > 0
                    ? "border-[#00E5FF] bg-[#00E5FF]/15 text-[#00E5FF] hover:bg-[#00E5FF]/25 hover:shadow-[0_0_18px_-4px_rgba(0,229,255,0.75)]"
                    : "border-white/15 text-white/60 hover:border-[#EB5757]/50 hover:text-[#EB5757]"
                }`}
              >
                <Check className="w-3 h-3" /> APPLY {selectedIds.length > 0 ? `· ${selectedIds.length}` : ""}
              </button>
              {/* CLEAR pill — detach subject entirely (parallel to the pill's X). */}
              <button
                onClick={() => { setPickerOpen(false); onClear?.(); }}
                data-testid="subject-lock-picker-clear"
                title="Detach the current subject(s) and reset the lock"
                className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-[#EB5757]/40 text-[#EB5757] hover:bg-[#EB5757]/10 hover:border-[#EB5757] hover:shadow-[0_0_16px_-4px_rgba(235,87,87,0.6)] transition-all font-mono-hy text-[9px] tracking-[0.18em]"
              >
                <Trash2 className="w-3 h-3" /> CLEAR
              </button>
              <button
                onClick={() => setPickerOpen(false)}
                data-testid="subject-lock-picker-close"
                className="w-6 h-6 rounded-full border border-white/[0.08] hover:border-white/30 flex items-center justify-center text-white transition-all"
                aria-label="Close picker"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* ── LOCKED NOW strip — shows what's currently in the lock with
              per-thumb X delete so users can detach a single subject without
              clearing the whole lock. Appears whether lock came from feed
              or upload (both routes populate `uploaded_urls`). ───────── */}
          {lockedUrls.length > 0 && (
            <div className="border-b border-white/[0.06]" data-testid="subject-lock-currently-locked">
              <div className="px-3.5 py-1.5 flex items-center justify-between gap-3 border-b border-white/[0.04]">
                <div className="font-mono-hy text-[8.5px] text-[#F2C94C] tracking-[0.2em] flex items-center gap-1.5">
                  <Lock className="w-2.5 h-2.5" /> LOCKED NOW
                </div>
                <div className="font-mono-hy text-[8.5px] text-[#F2C94C] tracking-[0.2em]">
                  {lockedUrls.length} ACTIVE
                </div>
              </div>
              <div className="flex items-center gap-3 px-5 py-5 overflow-x-auto max-w-full">
                {lockedUrls.map((u) => {
                  // Prefer the matching feed item for its prompt tooltip; fall back to raw URL.
                  const match = pastItems.find((p) => p.media_url === u);
                  return (
                    <div
                      key={u}
                      className="relative flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border border-[#F2C94C]/40 shadow-[0_0_18px_-4px_rgba(242,201,76,0.45)] transition-transform duration-200 ease-out hover:scale-[1.4] hover:z-30 hover:shadow-[0_0_28px_-4px_rgba(242,201,76,0.75)]"
                      data-testid={`subject-lock-active-tile-${u.slice(-24)}`}
                      title={match?.prompt || "Locked subject"}
                    >
                      <img src={u} alt="locked subject" className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/50 via-transparent to-transparent" />
                      {/* Delete X — removes just this subject from the active lock */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveLockedUrl?.(u);
                        }}
                        data-testid={`subject-lock-remove-${u.slice(-24)}`}
                        aria-label="Remove this subject from lock"
                        title="Remove this subject from lock"
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#EB5757] text-white flex items-center justify-center shadow-[0_0_12px_-2px_rgba(235,87,87,0.9)] hover:bg-[#ff6b6b] hover:scale-110 transition-all"
                      >
                        <X className="w-3 h-3" strokeWidth={3} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {targets.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <div className="font-mono-hy text-[10px] text-white tracking-widest">NO OTHER SUBJECTS IN THIS SESSION YET</div>
              <div className="font-body text-[11px] text-white mt-1">Generate a new subject to see it here.</div>
            </div>
          ) : (
            <>
              <div className="px-3.5 py-1.5 flex items-center justify-between gap-3 border-b border-white/[0.04]">
                <div className="font-mono-hy text-[8.5px] text-white/60 tracking-[0.2em]">
                  TAP TO SELECT · UP TO {MAX_SUBJECTS}
                </div>
                <div className="font-mono-hy text-[8.5px] text-[#00E5FF] tracking-[0.2em]">
                  {selectedIds.length}/{MAX_SUBJECTS} SELECTED
                </div>
              </div>
              <div className="flex items-center gap-3 px-5 py-5 overflow-x-auto max-w-full">
                {targets.map((it) => {
                  const idx = selectedIds.indexOf(it.id);
                  const isSelected = idx !== -1;
                  const capReached = !isSelected && selectedIds.length >= MAX_SUBJECTS;
                  return (
                    <div
                      key={it.id}
                      className={`relative flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border transition-all duration-200 ease-out group ${
                        isSelected
                          ? "border-[#00E5FF] shadow-[0_0_22px_-4px_rgba(0,229,255,0.8)] ring-2 ring-[#00E5FF]/60 hover:scale-[1.4] hover:z-30 hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.95)]"
                          : capReached
                            ? "border-white/[0.05] opacity-40 cursor-not-allowed"
                            : "border-white/[0.1] hover:border-[#00E5FF] hover:scale-[1.4] hover:z-30 hover:shadow-[0_0_28px_-4px_rgba(0,229,255,0.75)]"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (capReached) return;
                          toggleSelect(it.id);
                        }}
                        disabled={capReached}
                        data-testid={`subject-lock-swap-${it.id}`}
                        data-selected={isSelected ? "true" : "false"}
                        title={capReached ? `Max ${MAX_SUBJECTS} subjects` : (it.prompt || "Toggle this subject")}
                        className="absolute inset-0 w-full h-full"
                      >
                        <img src={it.media_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute bottom-1 left-1 right-1 font-mono-hy text-[8px] text-[#00E5FF] truncate opacity-0 group-hover:opacity-100 transition-opacity">
                          {(it.prompt || "").slice(0, 24)}
                        </div>
                      </button>
                      {/* Order badge when selected — shows the pick order */}
                      {isSelected && (
                        <div
                          className="absolute top-1 left-1 w-5 h-5 rounded-full bg-[#00E5FF] text-[#0A0A0A] flex items-center justify-center shadow-[0_0_12px_-2px_rgba(0,229,255,0.9)] pointer-events-none"
                          data-testid={`subject-lock-swap-check-${it.id}`}
                        >
                          <span className="font-mono-hy text-[9px] font-bold leading-none">{idx + 1}</span>
                        </div>
                      )}
                      {/* Delete X — unstages this pick (only visible when selected) */}
                      {isSelected && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIds((prev) => prev.filter((x) => x !== it.id));
                          }}
                          data-testid={`subject-lock-unstage-${it.id}`}
                          aria-label="Remove from selection"
                          title="Remove from selection"
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#EB5757] text-white flex items-center justify-center shadow-[0_0_12px_-2px_rgba(235,87,87,0.9)] hover:bg-[#ff6b6b] hover:scale-110 transition-all"
                        >
                          <X className="w-3 h-3" strokeWidth={3} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* The pill — dimmed when the subject is toggled off */}
      <div
        className={`pointer-events-auto inline-flex items-center gap-2.5 pl-2.5 pr-2 py-2 rounded-full backdrop-blur-2xl transition-all border ${
          enabled
            ? "border-[#00E5FF]/45 bg-[#00E5FF]/[0.08] shadow-[0_0_36px_-8px_rgba(0,229,255,0.6)]"
            : "border-white/[0.12] bg-black/40 opacity-55 grayscale"
        }`}
        data-testid={`subject-lock-pill-body-${enabled ? "on" : "off"}`}
      >
        {/* Thumb */}
        <div className={`relative w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border ${enabled ? "border-[#00E5FF]/50" : "border-white/20"}`}>
          <img src={lock.url || lock.uploaded_url} alt="subject" className="absolute inset-0 w-full h-full object-cover" />
          {enabled && <div className="absolute inset-0 bg-gradient-to-tr from-[#00E5FF]/25 via-transparent to-transparent" />}
          <div className={`absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center ${
            enabled
              ? "bg-[#00E5FF] text-[#0A0A0A] shadow-[0_0_10px_-1px_rgba(0,229,255,0.9)]"
              : "bg-white/30 text-[#0A0A0A]"
          }`}>
            <Lock className="w-2 h-2" strokeWidth={3} />
          </div>
          {/* +N badge when extra subjects are locked */}
          {extraCount > 0 && (
            <div
              className={`absolute -bottom-0.5 -left-0.5 min-w-[14px] h-[14px] px-[3px] rounded-full flex items-center justify-center font-mono-hy text-[8px] font-bold leading-none ${
                enabled
                  ? "bg-[#F2C94C] text-[#0A0A0A] shadow-[0_0_10px_-1px_rgba(242,201,76,0.9)]"
                  : "bg-white/40 text-[#0A0A0A]"
              }`}
              data-testid="subject-lock-extra-count"
              title={`${lockedUrls.length} subjects locked`}
            >
              +{extraCount}
            </div>
          )}
        </div>

        {/* Copy */}
        <div className="min-w-0 max-w-[240px]">
          <div className={`font-mono-hy text-[9px] tracking-[0.22em] leading-none flex items-center gap-1 ${enabled ? "text-[#00E5FF]" : "text-white/60"}`}>
            <Sparkles className="w-2.5 h-2.5" />
            {enabled
              ? (lockedUrls.length > 1 ? `SUBJECT LOCKED · ${lockedUrls.length} SELECTED` : "SUBJECT LOCKED")
              : (lockedUrls.length > 1 ? `SUBJECT PAUSED · ${lockedUrls.length} SELECTED` : "SUBJECT PAUSED")}
            <span className="opacity-60">· {lock.source === "generation" ? "AUTO" : "UPLOAD"}</span>
          </div>
          {lock.prompt && (
            <div className={`font-body text-[11px] leading-tight truncate mt-0.5 ${enabled ? "text-white" : "text-white/60"}`} title={lock.prompt}>
              {lock.prompt}
            </div>
          )}
        </div>

        {/* On/Off toggle — new. Preserves attachment but skips sending it. */}
        {onToggle && (
          <button
            onClick={() => onToggle(!enabled)}
            data-testid="subject-lock-toggle"
            aria-pressed={enabled}
            title={enabled ? "Pause subject lock (kept attached, not sent)" : "Resume subject lock — will be sent again"}
            className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full border font-mono-hy text-[9px] tracking-[0.18em] transition-all flex-shrink-0 ${
              enabled
                ? "border-[#F2C94C]/45 text-[#F2C94C] hover:bg-[#F2C94C]/10 hover:border-[#F2C94C] hover:shadow-[0_0_18px_-4px_rgba(242,201,76,0.55)]"
                : "border-white/25 text-white/80 hover:border-[#F2C94C] hover:text-[#F2C94C] hover:bg-[#F2C94C]/10"
            }`}
          >
            <Power className="w-3 h-3" />
            {enabled ? "ON" : "OFF"}
          </button>
        )}

        {/* Replace — only useful when the subject is active */}
        {(onReplace || onApplyMulti) && enabled && (
          <button
            onClick={() => setPickerOpen((v) => !v)}
            data-testid="subject-lock-replace"
            title={`Swap subject (${targets.length} available)`}
            className="hidden sm:inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-[#00E5FF]/30 hover:border-[#00E5FF] hover:bg-[#00E5FF]/10 hover:shadow-[0_0_18px_-4px_rgba(0,229,255,0.55)] transition-all text-[#00E5FF] font-mono-hy text-[9px] tracking-[0.18em] flex-shrink-0"
          >
            <Replace className="w-3 h-3" />
            REPLACE
            {targets.length > 0 && <span className="ml-0.5 opacity-70">· {targets.length}</span>}
          </button>
        )}

        {/* Clear */}
        <button
          onClick={onClear}
          data-testid="subject-lock-clear"
          title="Clear locked subject — future generations won't preserve this identity"
          className="w-7 h-7 rounded-full border border-white/[0.1] hover:border-[#EB5757] hover:text-[#EB5757] hover:shadow-[0_0_18px_-4px_rgba(235,87,87,0.55)] text-white transition-all flex items-center justify-center flex-shrink-0"
          aria-label="Clear subject"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default SubjectLockPill;
